/**
 * Command UI Component - VRP System v4
 * Enhanced mobile UX and VRP design system compliance
 */
import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
      className
    )}
    data-slot="command"
    {...props}
  />
))
Command.displayName = 'Command'

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    showSearchIcon?: boolean
  }
>(({ className, showSearchIcon = true, ...props }, ref) => (
  <div className="flex items-center border-b px-4" data-slot="command-input-wrapper">
    {showSearchIcon && <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />}
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'flex w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
        // Enhanced mobile touch targets - VRP Design System compliance
        'min-h-[44px] sm:min-h-[40px] touch-manipulation',
        // VRP Design System - Typography (Size 4: small text, Regular weight)
        'text-sm font-normal',
        // Mobile-specific optimizations
        'focus:outline-none focus:ring-0', // Remove focus ring on mobile for native feel
        className
      )}
      data-slot="command-input"
      // Enhanced mobile keyboard behavior
      autoCapitalize="none"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      {...props}
    />
  </div>
))
CommandInput.displayName = 'CommandInput'

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(
      'max-h-[300px] overflow-y-auto overflow-x-hidden',
      // Enhanced mobile scrolling with momentum and overscroll behavior
      'overscroll-behavior-y-contain scroll-smooth',
      // iOS-style smooth scrolling
      '[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-transparent',
      '[&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full',
      // Hide scrollbar on mobile for cleaner look
      'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border',
      'sm:scrollbar-thumb-muted-foreground/20',
      className
    )}
    data-slot="command-list"
    {...props}
  />
))
CommandList.displayName = 'CommandList'

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={cn(
      'py-6 text-center text-muted-foreground',
      // VRP Design System compliance - Typography
      'text-sm font-normal', // Size 4 (small text), Regular weight
      // Enhanced mobile spacing following 8pt grid system
      'px-4 py-8 sm:py-6',
      className
    )}
    data-slot="command-empty"
    {...props}
  />
))
CommandEmpty.displayName = 'CommandEmpty'

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm px-2 py-2 text-sm outline-none',
      'aria-selected:bg-accent aria-selected:text-accent-foreground',
      'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      // Enhanced mobile touch targets - VRP Design System compliance
      'min-h-[44px] sm:min-h-[36px] touch-manipulation',
      // VRP Design System - 8pt grid system spacing
      'px-4 py-2',
      // VRP Design System - Typography
      'text-sm font-normal', // Size 4 (small text), Regular weight
      // Enhanced mobile interaction states
      'active:scale-[0.98] transition-transform duration-75',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // Better hover states for devices with hover capability
      'hover:bg-accent hover:text-accent-foreground',
      '@media (hover: none) { hover:bg-transparent hover:text-current }',
      className
    )}
    data-slot="command-item"
    {...props}
  />
))
CommandItem.displayName = 'CommandItem'

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'ml-auto text-sm tracking-widest text-muted-foreground',
        // VRP Design System compliance
        'text-sm font-normal', // Size 4 (small text), Regular weight
        // Hide on mobile for cleaner interface
        'hidden sm:inline-block',
        className
      )}
      data-slot="command-shortcut"
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn(
      '-mx-1 h-px bg-border',
      // VRP Design System - 8pt grid system spacing
      'my-2',
      className
    )}
    data-slot="command-separator"
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-foreground',
      // Enhanced mobile group styling with VRP spacing
      'px-4 py-2',
      '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2',
      '[&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-semibold',
      '[&_[cmdk-group-heading]]:text-muted-foreground',
      // VRP Design System compliance for group headings
      '[&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-semibold', // Size 4, Semibold
      className
    )}
    data-slot="command-group"
    {...props}
  />
))
CommandGroup.displayName = CommandPrimitive.Group.displayName

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
